import { useState, useMemo, useCallback } from "react";
import { cn } from "../lib/utils";
import {
  IconGauge,
  IconMoneyBill,
  IconCircleChartLine,
  IconProgressBar,
} from "nucleo-glass";
import { NucleoIcon } from "../components/ui/NucleoIcon";
import type { ReactNode } from "react";
import type { KpiSnapshot } from "../data/types";
import { Group } from "@visx/group";
import { scaleLinear, scaleBand } from "@visx/scale";
import { LinePath, AreaClosed, Bar } from "@visx/shape";
import { curveMonotoneX } from "@visx/curve";
import { useTooltip, TooltipWithBounds } from "@visx/tooltip";
import { ParentSize } from "@visx/responsive";
import { LinearGradient } from "@visx/gradient";

interface ScorecardPageProps {
  kpis: KpiSnapshot[];
}

const weekLabels = ["W0", "W1", "W2", "W3", "W4"];

const metricsConfig = [
  { key: "leadsNew" as const, label: "New Leads" },
  { key: "demosBooked" as const, label: "Demos Booked" },
  { key: "demosCompleted" as const, label: "Demos Completed" },
  { key: "trialsStarted" as const, label: "Trials Started" },
  { key: "closedWon" as const, label: "Closed Won" },
  { key: "closedLost" as const, label: "Closed Lost" },
  { key: "newMrrEur" as const, label: "New MRR (€)" },
  { key: "trialToPaidPct" as const, label: "Trial → Paid %" },
  { key: "avgTimeToValueHours" as const, label: "Avg Time-to-Value (h)" },
  { key: "winRatePct" as const, label: "Win Rate %" },
  { key: "activityCompletionPct" as const, label: "Activity Completion %" },
  { key: "followupCoveragePct" as const, label: "Follow-up Coverage %" },
];

// Color palette (Base16 Default Dark)
const C = {
  accent: "#7cafc2",
  success: "#a1b56c",
  warning: "#f7ca88",
  stroke: "#383838",
  bg: "#181818",
  surface: "#282828",
  text: "#d8d8d8",
  muted: "#585858",
};

// ── Hand-drawn axis/grid helpers (avoids visx Axis/Grid bundling issues) ──

function HGrid({ yScale, width, ticks }: { yScale: (v: number) => number; width: number; ticks: number[] }) {
  return (
    <>
      {ticks.map((t) => (
        <line key={t} x1={0} x2={width} y1={yScale(t)} y2={yScale(t)} stroke={C.stroke} strokeOpacity={0.5} />
      ))}
    </>
  );
}

function XLabels({ scale, top, data }: { scale: (k: string) => number | undefined; top: number; data: string[] }) {
  return (
    <>
      {data.map((d) => {
        const x = scale(d);
        if (x === undefined) return null;
        return (
          <text key={d} x={x} y={top + 16} fill={C.muted} fontSize={12} textAnchor="middle">
            {d}
          </text>
        );
      })}
    </>
  );
}

function YLabels({ scale, ticks, format }: { scale: (v: number) => number; ticks: number[]; format?: (v: number) => string }) {
  const fmt = format ?? ((v: number) => String(v));
  return (
    <>
      {ticks.map((t) => (
        <text key={t} x={-6} y={scale(t) + 4} fill={C.muted} fontSize={12} textAnchor="end">
          {fmt(t)}
        </text>
      ))}
    </>
  );
}

function niceTicks(max: number, count: number): number[] {
  if (max <= 0) return [0];
  const step = Math.ceil(max / count);
  const result: number[] = [];
  for (let i = 0; i <= max; i += step) result.push(i);
  if (result[result.length - 1] !== max && max > 0) result.push(Math.ceil(max / step) * step);
  return result;
}

// ── Main Component ──

export function ScorecardPage({ kpis }: ScorecardPageProps) {
  const [selectedWeek, setSelectedWeek] = useState<number | "all">("all");

  const sortedKpis = useMemo(
    () => [...kpis].sort((a, b) => a.weekStart.localeCompare(b.weekStart)),
    [kpis],
  );

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full space-y-[var(--spacing-6)]">

        {/* Week selector */}
        <div className="flex items-center gap-[var(--spacing-1)] px-[var(--spacing-4)] pt-[var(--spacing-4)]">
          <button
            onClick={() => setSelectedWeek("all")}
            className={cn(
              "px-[var(--spacing-3)] py-[var(--spacing-1_5)] rounded-[var(--radius-md)] text-[14px] font-medium cursor-pointer transition-colors duration-[var(--duration-fast)]",
              selectedWeek === "all"
                ? "bg-[var(--color-accent-subtle-bg)] text-[var(--color-accent)]"
                : "text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]",
            )}
          >
            All
          </button>
          {weekLabels.map((label, idx) => (
            <button
              key={label}
              onClick={() => setSelectedWeek(idx)}
              className={cn(
                "px-[var(--spacing-3)] py-[var(--spacing-1_5)] rounded-[var(--radius-md)] text-[14px] font-medium cursor-pointer transition-colors duration-[var(--duration-fast)]",
                selectedWeek === idx
                  ? "bg-[var(--color-accent-subtle-bg)] text-[var(--color-accent)]"
                  : "text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Charts grid */}
        <div className="grid grid-cols-2 gap-[var(--spacing-3)] px-[var(--spacing-4)]">
          <div className="animate-stagger-in" style={{ animationDelay: "0ms" }}>
            <ChartCard title="Pipeline Funnel" icon={<NucleoIcon icon={<IconGauge size={16} />} />}>
              <ParentSize>
                {({ width }) => <FunnelChart width={width} height={180} kpis={sortedKpis} />}
              </ParentSize>
            </ChartCard>
          </div>

          <div className="animate-stagger-in" style={{ animationDelay: "50ms" }}>
            <ChartCard title="Revenue (€)" icon={<NucleoIcon icon={<IconMoneyBill size={16} />} />}>
              <ParentSize>
                {({ width }) => <RevenueBarChart width={width} height={180} kpis={sortedKpis} />}
              </ParentSize>
            </ChartCard>
          </div>

          <div className="animate-stagger-in" style={{ animationDelay: "100ms" }}>
            <ChartCard title="Conversion & Win Rate" icon={<NucleoIcon icon={<IconCircleChartLine size={16} />} />}>
              <ParentSize>
                {({ width }) => <ConversionChart width={width} height={180} kpis={sortedKpis} />}
              </ParentSize>
            </ChartCard>
          </div>

          <div className="animate-stagger-in" style={{ animationDelay: "150ms" }}>
            <ChartCard title="Execution Health" icon={<NucleoIcon icon={<IconProgressBar size={16} />} />}>
              <ParentSize>
                {({ width }) => <ExecutionChart width={width} height={180} kpis={sortedKpis} />}
              </ParentSize>
            </ChartCard>
          </div>
        </div>

        {/* Data table */}
        <div className="border-t border-[var(--color-border-stroke)] overflow-hidden">
          <table className="w-full text-[14px]">
            <thead>
              <tr className="border-b border-[var(--color-border-stroke)]">
                <th className="text-left font-semibold text-[var(--color-text-muted)] px-[var(--spacing-4)] py-[var(--spacing-2)]">Metric</th>
                {sortedKpis.map((_, idx) => (
                  <th key={idx} className="text-right font-semibold text-[var(--color-text-muted)] px-[var(--spacing-4)] py-[var(--spacing-2)] w-[80px]">
                    {weekLabels[idx]}
                  </th>
                ))}
                <th className="text-right font-semibold text-[var(--color-text-primary)] px-[var(--spacing-4)] py-[var(--spacing-2)] w-[80px]">Total</th>
              </tr>
            </thead>
            <tbody>
              {metricsConfig.map((metric, metricIdx) => {
                const values = sortedKpis.map((kpi) => kpi[metric.key] as number);
                const isPercent = metric.key.endsWith("Pct");
                const total = isPercent
                  ? (values.filter((v) => v > 0).length > 0
                      ? Math.round(values.reduce((s, v) => s + v, 0) / values.filter((v) => v > 0).length)
                      : 0)
                  : values.reduce((s, v) => s + v, 0);

                return (
                  <tr
                    key={metric.key}
                    className={cn(
                      "border-b border-[var(--color-border-stroke)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors",
                      metricIdx < 10 && "animate-row-in",
                    )}
                    style={metricIdx < 10 ? { animationDelay: `${metricIdx * 20}ms` } : undefined}
                  >
                    <td className="px-[var(--spacing-4)] py-[var(--spacing-2)] text-[var(--color-text-primary)]">{metric.label}</td>
                    {values.map((val, idx) => (
                      <td key={idx} className="px-[var(--spacing-4)] py-[var(--spacing-2)] text-right tabular-nums text-[var(--color-text-secondary)]">
                        {val === 0 ? <span className="text-[var(--color-text-muted)]">—</span> : val}
                      </td>
                    ))}
                    <td className="px-[var(--spacing-4)] py-[var(--spacing-2)] text-right tabular-nums font-semibold text-[var(--color-text-primary)]">
                      {total === 0 ? "—" : isPercent ? `${total}%` : total}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Chart Card wrapper ──

function ChartCard({ title, icon, children }: { title: string; icon?: ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)] p-[var(--spacing-4)]">
      <div className="flex items-center gap-[var(--spacing-2)] mb-[var(--spacing-3)]">
        {icon && <span className="text-[var(--color-text-muted)] shrink-0">{icon}</span>}
        <span className="text-[14px] font-semibold text-[var(--color-text-primary)]">{title}</span>
      </div>
      <div className="w-full" style={{ minHeight: 180 }}>
        {children}
      </div>
    </div>
  );
}

// ── Funnel Area Chart ──

function FunnelChart({ width, height, kpis }: { width: number; height: number; kpis: KpiSnapshot[] }) {
  const m = { top: 12, right: 16, bottom: 28, left: 40 };
  if (width < 100) return null;

  const iW = width - m.left - m.right;
  const iH = height - m.top - m.bottom;

  const data = kpis.map((k, i) => ({
    week: weekLabels[i] ?? `W${i}`,
    demos: k.demosBooked,
    closed: k.closedWon,
  }));

  const xScale = scaleBand({ domain: data.map((d) => d.week), range: [0, iW], padding: 0.2 });
  const xMid = (d: typeof data[0]) => (xScale(d.week) ?? 0) + xScale.bandwidth() / 2;
  const maxY = Math.max(10, ...data.map((d) => Math.max(d.demos, d.closed)));
  const yScale = scaleLinear({ domain: [0, maxY], range: [iH, 0], nice: true });
  const ticks = niceTicks(maxY, 4);

  return (
    <svg width={width} height={height}>
      <LinearGradient id="area-accent" from={C.accent} to={C.accent} fromOpacity={0.3} toOpacity={0.05} />
      <LinearGradient id="area-success" from={C.success} to={C.success} fromOpacity={0.3} toOpacity={0.05} />
      <Group left={m.left} top={m.top}>
        <HGrid yScale={yScale} width={iW} ticks={ticks} />

        <g className="animate-stagger-in" style={{ animationDelay: "0ms" }}>
          <AreaClosed data={data} x={xMid} y={(d) => yScale(d.demos)} yScale={yScale} fill="url(#area-accent)" curve={curveMonotoneX} />
          <LinePath data={data} x={xMid} y={(d) => yScale(d.demos)} stroke={C.accent} strokeWidth={2} curve={curveMonotoneX} />
        </g>

        <g className="animate-stagger-in" style={{ animationDelay: "100ms" }}>
          <AreaClosed data={data} x={xMid} y={(d) => yScale(d.closed)} yScale={yScale} fill="url(#area-success)" curve={curveMonotoneX} />
          <LinePath data={data} x={xMid} y={(d) => yScale(d.closed)} stroke={C.success} strokeWidth={2} curve={curveMonotoneX} />
        </g>

        {data.map((d, i) => (
          <g key={i} className="animate-stagger-in" style={{ animationDelay: `${150 + i * 40}ms` }}>
            <circle cx={xMid(d)} cy={yScale(d.demos)} r={3} fill={C.accent} />
            <circle cx={xMid(d)} cy={yScale(d.closed)} r={3} fill={C.success} />
          </g>
        ))}

        <XLabels scale={(k) => { const v = xScale(k); return v !== undefined ? v + xScale.bandwidth() / 2 : undefined; }} top={iH} data={data.map((d) => d.week)} />
        <YLabels scale={yScale} ticks={ticks} />
      </Group>

      <g transform={`translate(${width - 120}, 8)`}>
        <circle cx={0} cy={4} r={3} fill={C.accent} />
        <text x={8} y={8} fill={C.muted} fontSize={11}>Demos</text>
        <circle cx={55} cy={4} r={3} fill={C.success} />
        <text x={63} y={8} fill={C.muted} fontSize={11}>Closed</text>
      </g>
    </svg>
  );
}

// ── Revenue Bar Chart ──

function RevenueBarChart({ width, height, kpis }: { width: number; height: number; kpis: KpiSnapshot[] }) {
  const m = { top: 12, right: 16, bottom: 28, left: 48 };
  if (width < 100) return null;

  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip<{ week: string; value: number }>();

  const iW = width - m.left - m.right;
  const iH = height - m.top - m.bottom;

  const data = kpis.map((k, i) => ({ week: weekLabels[i] ?? `W${i}`, value: k.newMrrEur }));

  const xScale = scaleBand({ domain: data.map((d) => d.week), range: [0, iW], padding: 0.35 });
  const maxY = Math.max(100, ...data.map((d) => d.value));
  const yScale = scaleLinear({ domain: [0, maxY], range: [iH, 0], nice: true });
  const ticks = niceTicks(maxY, 4);

  const handleMouse = useCallback(
    (d: typeof data[0], barX: number) => {
      showTooltip({
        tooltipData: d,
        tooltipLeft: barX + m.left + xScale.bandwidth() / 2,
        tooltipTop: yScale(d.value) + m.top - 8,
      });
    },
    [showTooltip, m.left, m.top, xScale, yScale],
  );

  return (
    <div className="relative">
      <svg width={width} height={height}>
        <LinearGradient id="bar-grad" from={C.accent} to={C.accent} fromOpacity={0.9} toOpacity={0.5} />
        <Group left={m.left} top={m.top}>
          <HGrid yScale={yScale} width={iW} ticks={ticks} />

          {data.map((d, i) => {
            const barX = xScale(d.week) ?? 0;
            const barH = iH - yScale(d.value);
            return (
              <g key={d.week} className="animate-bar-grow" style={{ animationDelay: `${i * 60}ms`, transformOrigin: `${barX + xScale.bandwidth() / 2}px ${iH}px` }}>
                <Bar x={barX} y={yScale(d.value)} width={xScale.bandwidth()} height={barH} fill="url(#bar-grad)" rx={4} onMouseMove={() => handleMouse(d, barX)} onMouseLeave={hideTooltip} />
              </g>
            );
          })}

          <XLabels scale={(k) => { const v = xScale(k); return v !== undefined ? v + xScale.bandwidth() / 2 : undefined; }} top={iH} data={data.map((d) => d.week)} />
          <YLabels scale={yScale} ticks={ticks} format={(v) => `€${v}`} />
        </Group>
      </svg>

      {tooltipOpen && tooltipData && (
        <TooltipWithBounds left={tooltipLeft} top={tooltipTop} style={{ background: C.surface, border: `1px solid ${C.stroke}`, borderRadius: 10, padding: "4px 8px", color: C.text, fontSize: 12, fontWeight: 500, pointerEvents: "none" }}>
          <span>{tooltipData.week}: €{tooltipData.value}</span>
        </TooltipWithBounds>
      )}
    </div>
  );
}

// ── Conversion Line Chart ──

function ConversionChart({ width, height, kpis }: { width: number; height: number; kpis: KpiSnapshot[] }) {
  const m = { top: 12, right: 16, bottom: 28, left: 40 };
  if (width < 100) return null;

  const iW = width - m.left - m.right;
  const iH = height - m.top - m.bottom;

  const data = kpis.map((k, i) => ({
    week: weekLabels[i] ?? `W${i}`,
    trialToPaid: k.trialToPaidPct,
    winRate: k.winRatePct,
  }));

  const xScale = scaleBand({ domain: data.map((d) => d.week), range: [0, iW], padding: 0.2 });
  const xMid = (d: typeof data[0]) => (xScale(d.week) ?? 0) + xScale.bandwidth() / 2;
  const maxY = Math.max(25, ...data.map((d) => Math.max(d.trialToPaid, d.winRate)));
  const yScale = scaleLinear({ domain: [0, maxY], range: [iH, 0], nice: true });
  const ticks = niceTicks(maxY, 4);

  return (
    <svg width={width} height={height}>
      <Group left={m.left} top={m.top}>
        <HGrid yScale={yScale} width={iW} ticks={ticks} />

        <g className="animate-stagger-in" style={{ animationDelay: "0ms" }}>
          <LinePath data={data} x={xMid} y={(d) => yScale(d.trialToPaid)} stroke={C.warning} strokeWidth={2} curve={curveMonotoneX} />
          {data.map((d, i) => (
            <circle key={`t-${i}`} cx={xMid(d)} cy={yScale(d.trialToPaid)} r={3.5} fill={C.warning} className="animate-stagger-in" style={{ animationDelay: `${80 + i * 40}ms` }} />
          ))}
        </g>

        <g className="animate-stagger-in" style={{ animationDelay: "100ms" }}>
          <LinePath data={data} x={xMid} y={(d) => yScale(d.winRate)} stroke={C.accent} strokeWidth={2} curve={curveMonotoneX} strokeDasharray="5,3" />
          {data.map((d, i) => (
            <circle key={`w-${i}`} cx={xMid(d)} cy={yScale(d.winRate)} r={3.5} fill={C.accent} stroke={C.bg} strokeWidth={1} className="animate-stagger-in" style={{ animationDelay: `${180 + i * 40}ms` }} />
          ))}
        </g>

        {/* Target line at 20% */}
        <line x1={0} x2={iW} y1={yScale(20)} y2={yScale(20)} stroke={C.success} strokeWidth={1} strokeDasharray="4,4" opacity={0.6} />
        <text x={iW + 4} y={yScale(20) + 3} fill={C.success} fontSize={10} opacity={0.7}>20%</text>

        <XLabels scale={(k) => { const v = xScale(k); return v !== undefined ? v + xScale.bandwidth() / 2 : undefined; }} top={iH} data={data.map((d) => d.week)} />
        <YLabels scale={yScale} ticks={ticks} format={(v) => `${v}%`} />
      </Group>

      <g transform={`translate(${width - 160}, 8)`}>
        <line x1={0} y1={4} x2={12} y2={4} stroke={C.warning} strokeWidth={2} />
        <text x={16} y={8} fill={C.muted} fontSize={11}>Trial→Paid</text>
        <line x1={80} y1={4} x2={92} y2={4} stroke={C.accent} strokeWidth={2} strokeDasharray="5,3" />
        <text x={96} y={8} fill={C.muted} fontSize={11}>Win Rate</text>
      </g>
    </svg>
  );
}

// ── Execution Health (horizontal bars) ──

function ExecutionChart({ width, height, kpis }: { width: number; height: number; kpis: KpiSnapshot[] }) {
  const m = { top: 12, right: 16, bottom: 12, left: 150 };
  if (width < 100) return null;

  const iW = width - m.left - m.right;
  const iH = height - m.top - m.bottom;

  const nonZero = (arr: number[]) => arr.filter((v) => v > 0);
  const avg = (arr: number[]) => {
    const nz = nonZero(arr);
    return nz.length > 0 ? Math.round(nz.reduce((s, v) => s + v, 0) / nz.length) : 0;
  };

  const metrics = [
    { label: "Activity Completion", value: avg(kpis.map((k) => k.activityCompletionPct)), target: 80, color: C.accent },
    { label: "Follow-up Coverage", value: avg(kpis.map((k) => k.followupCoveragePct)), target: 90, color: C.success },
    { label: "Trial → Paid", value: avg(kpis.map((k) => k.trialToPaidPct)), target: 20, color: C.warning },
    { label: "Win Rate", value: avg(kpis.map((k) => k.winRatePct)), target: 50, color: C.accent },
  ];

  const yScale = scaleBand({ domain: metrics.map((mm) => mm.label), range: [0, iH], padding: 0.35 });
  const xScale = scaleLinear({ domain: [0, 100], range: [0, iW] });

  return (
    <svg width={width} height={height}>
      <Group left={m.left} top={m.top}>
        {metrics.map((mm, mmIdx) => {
          const barY = yScale(mm.label) ?? 0;
          const barH = yScale.bandwidth();
          const barW = xScale(Math.min(100, mm.value));
          const targetX = xScale(mm.target);

          return (
            <g key={mm.label} className="animate-stagger-in" style={{ animationDelay: `${mmIdx * 60}ms` }}>
              <text x={-8} y={barY + barH / 2 + 4} fill={C.muted} fontSize={12} textAnchor="end">{mm.label}</text>
              <rect x={0} y={barY} width={iW} height={barH} rx={4} fill={C.stroke} fillOpacity={0.3} />
              <rect x={0} y={barY} width={barW} height={barH} rx={4} fill={mm.color} fillOpacity={0.8} className="animate-health-bar-fill" style={{ animationDelay: `${mmIdx * 60 + 80}ms`, transformOrigin: `0 ${barY + barH / 2}px` }} />
              <line x1={targetX} x2={targetX} y1={barY - 2} y2={barY + barH + 2} stroke={C.text} strokeWidth={1.5} strokeDasharray="3,2" opacity={0.4} />
              <text x={barW + 6} y={barY + barH / 2 + 4} fill={C.text} fontSize={12} fontWeight={600}>{mm.value}%</text>
            </g>
          );
        })}
      </Group>
    </svg>
  );
}
